import { useEffect, useRef, useState } from 'react';
import {
  ChevronDownIcon,
  ChevronUpDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/20/solid';
import { BuildingStorefrontIcon } from '@heroicons/react/24/outline';
import { Button } from '@tailwindComponents/Button';
import { FloxSpinner } from '@tailwindComponents/FloxSpinner';
import { SkuMenuOption } from '@views/SKUMaster/components/SkuActionsMenu';
import { Supplier } from './supplierTypes';
import { SupplierTableRow } from './SupplierTableRow';

export type SupplierSortColumn =
  | 'name'
  | 'supplierId'
  | 'sourceType'
  | 'certification'
  | 'readiness'
  | 'status';

export interface SupplierTableProps {
  suppliers: Supplier[];
  isLoading: boolean;
  buildMenuOptions: (supplier: Supplier) => SkuMenuOption[];
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  onAddSupplier: () => void;
  orderBy: SupplierSortColumn | null;
  isOrderAscending: boolean;
  onSortColumn: (column: SupplierSortColumn) => void;
}

const headerCell = 'px-4 py-3 text-xs font-medium text-gray-500';

export function SupplierTable({
  suppliers,
  isLoading,
  buildMenuOptions,
  hasActiveFilters,
  onClearFilters,
  onAddSupplier,
  orderBy,
  isOrderAscending,
  onSortColumn,
}: SupplierTableProps) {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const copyResetTimeout = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(copyResetTimeout.current), []);

  const handleToggleExpand = (id: string) => {
    setExpandedIds((previous) => {
      const next = new Set(previous);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleCopy = (supplier: Supplier) => {
    navigator.clipboard?.writeText(supplier.supplierId);
    setCopiedKey(supplier.id);
    clearTimeout(copyResetTimeout.current);
    copyResetTimeout.current = setTimeout(() => setCopiedKey(null), 1500);
  };

  const sortArrow = (column: SupplierSortColumn) => {
    if (orderBy === column) {
      return isOrderAscending ? (
        <ChevronUpIcon className='inline h-4 w-4' />
      ) : (
        <ChevronDownIcon className='inline h-4 w-4' />
      );
    }
    return <ChevronUpDownIcon className='inline h-4 w-4 text-gray-300' />;
  };

  const sortableHeader = (label: string, column: SupplierSortColumn) => (
    <th
      scope='col'
      onClick={() => onSortColumn(column)}
      className={`${headerCell} cursor-pointer text-left hover:text-gray-700`}
    >
      {label} {sortArrow(column)}
    </th>
  );

  return (
    <div className='overflow-x-auto rounded-lg border border-gray-200 bg-white'>
      <table className='w-full min-w-[920px] table-auto'>
        <thead className='bg-gray-50'>
          <tr>
            <th className='w-10 px-2'>
              <span className='sr-only'>Expand</span>
            </th>
            {sortableHeader('Name', 'name')}
            {sortableHeader('Supplier ID', 'supplierId')}
            {sortableHeader('Type', 'sourceType')}
            <th scope='col' className={`${headerCell} text-left`}>
              Brands supplied
            </th>
            {sortableHeader('Certification', 'certification')}
            {sortableHeader('Readiness', 'readiness')}
            {sortableHeader('Status', 'status')}
            <th scope='col' className={`${headerCell} text-right`}>
              Actions
            </th>
          </tr>
        </thead>
        <tbody className='divide-y divide-gray-200'>
          {isLoading && (
            <tr>
              <td colSpan={9} className='py-12 text-center'>
                <div className='flex flex-col items-center gap-3'>
                  <FloxSpinner size='md' />
                  <span className='text-sm text-gray-500'>
                    Loading suppliers...
                  </span>
                </div>
              </td>
            </tr>
          )}
          {!isLoading && suppliers.length === 0 && (
            <tr>
              <td colSpan={9} className='py-12 text-center'>
                <div className='flex flex-col items-center gap-3'>
                  <BuildingStorefrontIcon className='h-12 w-12 text-gray-300' />
                  <p className='text-sm font-medium text-gray-900'>
                    No suppliers found
                  </p>
                  {hasActiveFilters ? (
                    <>
                      <p className='text-sm text-gray-500'>
                        No suppliers match the current filters.
                      </p>
                      <Button
                        intent='tertiary'
                        size='sm'
                        label='Clear filters'
                        onClick={onClearFilters}
                      />
                    </>
                  ) : (
                    <>
                      <p className='text-sm text-gray-500'>
                        Add your first supplier to get started.
                      </p>
                      <Button
                        intent='primary'
                        size='sm'
                        label='Add supplier'
                        onClick={onAddSupplier}
                      />
                    </>
                  )}
                </div>
              </td>
            </tr>
          )}
          {!isLoading &&
            suppliers.map((supplier) => (
              <SupplierTableRow
                key={supplier.id}
                supplier={supplier}
                menuOptions={buildMenuOptions(supplier)}
                isCopied={copiedKey === supplier.id}
                onCopy={handleCopy}
                isExpanded={expandedIds.has(supplier.id)}
                onToggleExpand={handleToggleExpand}
              />
            ))}
        </tbody>
      </table>
    </div>
  );
}
